import { Injectable } from '@angular/core';
import { ArbreComposant, ArbreExplorer, FormatOptions } from 'src/app/model/arbre/arbre-composant';
import { jsPDF } from 'jspdf';
import { PDF_PARAMS, PDF_COLORS, PDF_TEXTS, PDFLogo } from './pdf-constants';

@Injectable({
  providedIn: 'root'
})
export class PdfService {

  constructor() { }

  private solvedColor!: string;

  private pageHeight = 290;
  private pageWidth = 203;
  private step = 60;
  private nextX = 10;
  private nextY = 0;

  /**
   * 
   * @param numSerie Serial number of machine
   * @param resolved true if breakdown has been solved, false otherwise
   * @param dateHeure string representation of french formatted date and time 
   * @param username user who requires the pdf
   * @param exploredPaths paths to write in the pdf
   * @param additionnalInfos additionnal comments to write in the pdf
   */
  public makePdf(numSerie: string, resolved: boolean, dateHeure: string, username: string, exploredPaths: ArbreExplorer[], additionnalInfos: any[]) { //, computedTree: MergeTree[]) {

    /** Reset fields */
    this.step = 60;
    this.nextX = 10;
    this.nextY = 0;

    const doc = new jsPDF(PDF_PARAMS.ORIENTATION, PDF_PARAMS.UNIT, PDF_PARAMS.FORMAT);

    this.writeHeaderInfos(doc, username, dateHeure, numSerie, resolved);

    doc.setTextColor(PDF_COLORS.BLACK);

    for (let element of exploredPaths) {
      for (let j = 0; j < element.path.length; j++) {
        let node = element.path[j];
        let opt = this.getTextFormatOptionsFromNodeType(node);
        doc.setTextColor(opt.color);

        let realText = doc.splitTextToSize(opt.text, this.pageWidth - this.nextX);

        this.addTextToDoc(doc, realText, this.nextX, (this.step + this.nextY));

        this.nextY += 7 * realText.length;
        this.nextX += 7;

        let additionnalComment = additionnalInfos.find(com => { return com.nodeId == node.id })

        if (j == element.path.length - 1 || additionnalComment) {
          this.addCommentToDoc(doc, element, additionnalComment);
        }
      }
    }

    doc.save('_' + numSerie + PDF_PARAMS.EXTENSION);
    doc.output('dataurlnewwindow');
  }

  private addCommentToDoc(doc: jsPDF, element: ArbreExplorer, additionnalComment: any) {
    let comment = element.comment;
    let pLevel = element.proofLevel;
    if (additionnalComment) {
      comment = additionnalComment.comment;
      pLevel = additionnalComment.proofLevel
    }
    const proofLevel = pLevel == 'S' ?
      { text: PDF_TEXTS.STRONG_PROOF, color: PDF_COLORS.BLUE }
      : { text: PDF_TEXTS.WEAK_PROOF, color: PDF_COLORS.RED };

    doc.setTextColor(PDF_COLORS.GREEN);

    let realComment = doc.splitTextToSize(PDF_TEXTS.COMM + comment, this.pageWidth - this.nextX);

    this.addTextToDoc(doc, realComment, this.nextX, (this.step + this.nextY));

    doc.setTextColor(proofLevel.color);

    this.addTextToDoc(doc, proofLevel.text, this.nextX, (this.step + this.nextY + 7 * realComment.length));

    if (!additionnalComment) {
      this.nextX = 10; // End of Path, reset left alignment
    }
    this.nextY += (7 + 7 * realComment.length);
  }

  /**
   * Write a 'header' to a given jsPDF doc
   * @param doc a valid jsPDF instance to write to
   * @param username 
   * @param dateHeure 
   * @param numSerie machine serial number
   * @param resolved boolean indicating if breakdown is solved or not
   */
  private writeHeaderInfos(doc: jsPDF, username: string, dateHeure: string, numSerie: string, resolved: boolean) {

    this.addImageLogo(doc);

    doc.text(PDF_TEXTS.AGENT + username, 40, 15);
    doc.text(PDF_TEXTS.DATE + dateHeure, 40, 25);
    doc.text(PDF_TEXTS.MACHINE + numSerie, 40, 35);

    /** Write solved state using dedicated color */
    this.setupSolved(doc, resolved)

    doc.setTextColor(PDF_COLORS.RED);
    doc.setFontSize(doc.getFontSize() + 10)
    doc.text(PDF_TEXTS.VERIFICATIONS, 80, 55)
    doc.setFontSize(doc.getFontSize() - 10)

  }

  addImageLogo(doc: jsPDF) {
    const logo = new Image();
    let logoHolder: PDFLogo = new PDFLogo();
    logo.src = logoHolder.logoSrc;
    doc.addImage(logo, logoHolder.posXY, logoHolder.posXY, logoHolder.widthHeight, logoHolder.widthHeight);
  }

  private setupSolved(doc: jsPDF, resolved: boolean) {
    let textSolved: string;
    if (resolved) {
      doc.setTextColor(PDF_COLORS.GREEN);
      textSolved = PDF_TEXTS.SOLVED;
    } else {
      doc.setTextColor(PDF_COLORS.RED);
      textSolved = PDF_TEXTS.NOT_SOLVED;
    }
    doc.text(textSolved, 10, 45);
  }

  private addTextToDoc(doc: jsPDF, text: string, nextX: number, nextY: number) {

    if (nextY > this.pageHeight) {
      doc.addPage();
      this.nextY = 10;
      this.step = 5;
    }

    doc.text(text, nextX, nextY);
  }

  private getTextFormatOptionsFromNodeType(node: ArbreComposant): FormatOptions {

    let options: FormatOptions = new FormatOptions(PDF_COLORS.BLACK, node.label, '');

    switch (node.type) {
      case 'Symptome':
        options.color = PDF_COLORS.BROWN;
        options.text = '* ' + node.label;
        break;
      case 'Réponse':
        options.color = PDF_COLORS.BLUE;
        options.text = node.label;
        break;
      default:
        options.color = PDF_COLORS.BLACK;
        options.text = node.label;
        break;
    }

    return options;
  }

}
